import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ConversationCategory,
  ConversationDetail,
  ConversationSummary,
  MessageAttachmentInput,
  MessagingContact,
} from "@ghalishor/contracts";
import type { IdentityApiClient } from "./auth/api-client";
import { identityErrorMessage } from "./auth/api-client";
import "./messaging-center.css";

type MessagingMode = "platform" | "business" | "customer";

const CATEGORY_LABELS: Record<ConversationCategory, string> = {
  general: "گفت‌وگوی عمومی",
  follow_up: "پیگیری درخواست",
  complaint: "شکایت و رسیدگی",
  payment: "مالی و پرداخت",
  service_request: "سفارش و خدمات",
  operations: "عملیات",
};

function jalali(value: string | null): string {
  if (!value) return "";
  return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

async function imageInput(file: File): Promise<MessageAttachmentInput> {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type))
    throw new Error("فقط تصویر JPEG، PNG یا WebP قابل ارسال است.");
  if (file.size > 5 * 1024 * 1024)
    throw new Error("حجم هر تصویر باید حداکثر ۵ مگابایت باشد.");
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("خواندن تصویر انجام نشد."));
    reader.readAsDataURL(file);
  });
  return {
    fileName: file.name,
    mimeType: file.type as MessageAttachmentInput["mimeType"],
    base64: dataUrl.slice(dataUrl.indexOf(",") + 1),
  };
}

function ProtectedImage(props: {
  api: IdentityApiClient;
  attachment: { id: string; fileName: string };
  onLoad?: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    void props.api.messagingAttachment(props.attachment.id).then((blob) => {
      objectUrl = URL.createObjectURL(blob);
      if (active) setUrl(objectUrl);
    });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [props.api, props.attachment.id]);
  return url ? (
    <a href={url} target="_blank" rel="noreferrer">
      <img
        src={url}
        alt={props.attachment.fileName}
        loading="lazy"
        onLoad={props.onLoad}
      />
    </a>
  ) : (
    <span className="messaging__image-loading">در حال دریافت تصویر…</span>
  );
}

export function MessagingCenter(props: {
  api: IdentityApiClient;
  mode: MessagingMode;
  businessId?: string | null;
}) {
  const [me, setMe] = useState<string | null>(null);
  const [contacts, setContacts] = useState<readonly MessagingContact[]>([]);
  const [conversations, setConversations] = useState<
    readonly ConversationSummary[]
  >([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [composer, setComposer] = useState("");
  const [attachments, setAttachments] = useState<
    readonly MessageAttachmentInput[]
  >([]);
  const [newAttachments, setNewAttachments] = useState<
    readonly MessageAttachmentInput[]
  >([]);
  const [newOpen, setNewOpen] = useState(false);
  const [targetKey, setTargetKey] = useState(
    props.mode === "customer" ? "support" : "",
  );
  const [subject, setSubject] = useState("");
  const [firstMessage, setFirstMessage] = useState("");
  const [category, setCategory] = useState<ConversationCategory>("general");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState("");
  const typingTimer = useRef<number | null>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);

  const scrollToBottom = useCallback((force = false) => {
    const node = messageListRef.current;
    if (!node || (!force && !stickToBottomRef.current)) return;
    window.requestAnimationFrame(() => {
      node.scrollTop = node.scrollHeight;
    });
  }, []);

  const refreshList = useCallback(async () => {
    const response = await props.api.listConversations();
    setConversations(response.items);
    const queryId = new URLSearchParams(window.location.search).get(
      "conversation",
    );
    setSelectedId(
      (current) => current ?? queryId ?? response.items[0]?.id ?? null,
    );
  }, [props.api]);

  const refreshDetail = useCallback(async () => {
    if (!selectedId) return;
    const response = await props.api.conversation(selectedId);
    setDetail(response);
    await props.api.markConversationRead(selectedId);
  }, [props.api, selectedId]);

  useEffect(() => {
    let active = true;
    setError(null);
    void Promise.all([
      props.api.currentUser(),
      props.api.messagingContacts({ q: search, businessId: props.businessId }),
      props.api.listConversations(),
    ])
      .then(([user, contactResponse, conversationResponse]) => {
        if (!active) return;
        setMe(user.user.id);
        setContacts(contactResponse.items);
        setConversations(conversationResponse.items);
        const queryId = new URLSearchParams(window.location.search).get(
          "conversation",
        );
        setSelectedId(
          (current) =>
            current ?? queryId ?? conversationResponse.items[0]?.id ?? null,
        );
      })
      .catch(
        (reason: unknown) => active && setError(identityErrorMessage(reason)),
      );
    return () => {
      active = false;
    };
  }, [props.api, props.businessId, search]);

  useEffect(() => {
    void refreshDetail().catch((reason: unknown) =>
      setError(identityErrorMessage(reason)),
    );
  }, [refreshDetail]);

  useEffect(() => {
    stickToBottomRef.current = true;
    scrollToBottom(true);
  }, [selectedId, scrollToBottom]);

  useEffect(() => {
    scrollToBottom();
  }, [detail?.messages.length, scrollToBottom]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void refreshList();
      void refreshDetail();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [refreshList, refreshDetail]);

  const targetOptions = useMemo(() => {
    const items = contacts.filter((contact) => {
      if (props.mode === "customer") return contact.kind === "business";
      return true;
    });
    return items.map((contact) => ({
      key: `${contact.userId}|${contact.businessId ?? ""}|${contact.serviceRequestId ?? ""}`,
      contact,
    }));
  }, [contacts, props.mode]);

  async function selectImages(files: FileList | null, forNew = false) {
    if (!files?.length) return;
    try {
      const next = await Promise.all(
        Array.from(files).slice(0, 4).map(imageInput),
      );
      const total = next.reduce(
        (sum, item) => sum + Math.ceil(item.base64.length * 0.75),
        0,
      );
      if (total > 10 * 1024 * 1024)
        throw new Error("حجم مجموع تصاویر باید حداکثر ۱۰ مگابایت باشد.");
      if (forNew) setNewAttachments(next);
      else setAttachments(next);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "انتخاب تصویر انجام نشد.",
      );
    }
  }

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const option = targetOptions.find(
        (item) => item.key === targetKey,
      )?.contact;
      const customerSupport =
        props.mode === "customer" && targetKey === "support";
      if (!customerSupport && !option) throw new Error("مخاطب را انتخاب کنید.");
      const response = await props.api.createConversation(
        {
          targetUserId: props.mode === "customer" ? undefined : option?.userId,
          audience: customerSupport
            ? "platform_support"
            : props.mode === "customer"
              ? "business_team"
              : undefined,
          businessId: option?.businessId ?? props.businessId ?? undefined,
          serviceRequestId: option?.serviceRequestId ?? undefined,
          category,
          subject,
          message: firstMessage,
          attachments: newAttachments,
        },
        props.businessId,
      );
      setNewOpen(false);
      setSubject("");
      setFirstMessage("");
      setNewAttachments([]);
      setSelectedId(response.id);
      setDetail(response);
      await refreshList();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : identityErrorMessage(reason),
      );
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    if (!selectedId || (!composer.trim() && attachments.length === 0)) return;
    setBusy(true);
    setError(null);
    try {
      await props.api.sendConversationMessage(selectedId, {
        body: composer.trim(),
        attachments,
      });
      setComposer("");
      setAttachments([]);
      stickToBottomRef.current = true;
      await Promise.all([refreshDetail(), refreshList()]);
      scrollToBottom(true);
    } catch (reason) {
      setError(identityErrorMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  function beginEdit(messageId: string, body: string) {
    setEditingId(messageId);
    setEditingBody(body);
  }

  async function saveEdit() {
    if (!selectedId || !editingId || !editingBody.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await props.api.updateConversationMessage(selectedId, editingId, {
        body: editingBody.trim(),
      });
      setEditingId(null);
      setEditingBody("");
      await Promise.all([refreshDetail(), refreshList()]);
    } catch (reason) {
      setError(identityErrorMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  async function removeMessage(messageId: string) {
    if (!selectedId) return;
    if (!window.confirm("این پیام و تصاویر پیوست‌شده حذف شود؟")) return;
    setBusy(true);
    setError(null);
    try {
      await props.api.deleteConversationMessage(selectedId, messageId);
      if (editingId === messageId) {
        setEditingId(null);
        setEditingBody("");
      }
      await Promise.all([refreshDetail(), refreshList()]);
    } catch (reason) {
      setError(identityErrorMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  function typing(value: string) {
    setComposer(value);
    if (!selectedId) return;
    void props.api.setConversationTyping(selectedId, true);
    if (typingTimer.current) window.clearTimeout(typingTimer.current);
    typingTimer.current = window.setTimeout(
      () => void props.api.setConversationTyping(selectedId, false),
      1500,
    );
  }

  return (
    <section className="messaging" dir="rtl">
      <header className="messaging__top">
        <div>
          <h2>مرکز پیام زنده</h2>
          <p>گفت‌وگو، پیگیری و تصویر؛ همراه با اعلان هر پیام</p>
        </div>
        <button type="button" onClick={() => setNewOpen((value) => !value)}>
          + گفت‌وگوی جدید
        </button>
      </header>
      {error ? <div className="messaging__error">{error}</div> : null}
      {newOpen ? (
        <div className="messaging__new">
          <label>
            مخاطب
            <select
              value={targetKey}
              onChange={(event) => setTargetKey(event.target.value)}
            >
              <option value="">انتخاب مخاطب</option>
              {props.mode === "customer" ? (
                <option value="support">پشتیبانی آنلاین پلتفرم</option>
              ) : null}
              {targetOptions.map(({ key, contact }) => (
                <option value={key} key={key}>
                  {contact.displayName} —{" "}
                  {contact.businessName ?? contact.roleLabel}
                </option>
              ))}
            </select>
          </label>
          <label>
            موضوع
            <input
              value={subject}
              maxLength={140}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="موضوع روشن و کوتاه"
            />
          </label>
          <label>
            دسته‌بندی
            <select
              value={category}
              onChange={(event) =>
                setCategory(event.target.value as ConversationCategory)
              }
            >
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="messaging__wide">
            پیام نخست
            <textarea
              value={firstMessage}
              onChange={(event) => setFirstMessage(event.target.value)}
              placeholder="درخواست یا توضیح خود را بنویسید…"
            />
          </label>
          <label className="messaging__file">
            پیوست تصویر
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={(event) => void selectImages(event.target.files, true)}
            />
          </label>
          {newAttachments.length ? (
            <small>
              {newAttachments.length.toLocaleString("fa-IR")} تصویر انتخاب شد
            </small>
          ) : null}
          <button
            type="button"
            disabled={
              busy ||
              subject.trim().length < 3 ||
              firstMessage.trim().length < 1
            }
            onClick={() => void create()}
          >
            ایجاد و ارسال
          </button>
        </div>
      ) : null}
      <div className="messaging__layout">
        <aside className="messaging__threads">
          <input
            className="messaging__search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="جست‌وجوی مخاطب…"
          />
          {conversations.map((conversation) => (
            <button
              type="button"
              key={conversation.id}
              className={selectedId === conversation.id ? "is-active" : ""}
              onClick={() => setSelectedId(conversation.id)}
            >
              <span>
                <strong>{conversation.subject}</strong>
                {conversation.unreadCount ? (
                  <b>{conversation.unreadCount.toLocaleString("fa-IR")}</b>
                ) : null}
              </span>
              <small>
                {conversation.participants
                  .filter((p) => p.userId !== me)
                  .map((p) => p.displayName)
                  .join("، ")}
              </small>
              <em>{conversation.lastMessagePreview ?? "بدون پیام"}</em>
            </button>
          ))}
          {!conversations.length ? (
            <p className="messaging__empty">هنوز گفت‌وگویی ندارید.</p>
          ) : null}
        </aside>
        <main className="messaging__chat">
          {detail ? (
            <>
              <header className="messaging__chat-head">
                <div>
                  <h3>{detail.subject}</h3>
                  <span>
                    {CATEGORY_LABELS[detail.category]}
                    {detail.businessName ? ` · ${detail.businessName}` : ""}
                  </span>
                </div>
                <div className="messaging__presence">
                  {detail.participants
                    .filter((p) => p.userId !== me)
                    .some((p) => p.isOnline)
                    ? "● آنلاین"
                    : "آفلاین"}
                </div>
              </header>
              <div
                className="messaging__messages"
                ref={messageListRef}
                onScroll={(event) => {
                  const node = event.currentTarget;
                  stickToBottomRef.current =
                    node.scrollHeight - node.scrollTop - node.clientHeight < 96;
                }}
              >
                {detail.messages.map((message) => (
                  <article
                    key={message.id}
                    className={message.senderUserId === me ? "is-mine" : ""}
                  >
                    <div className="messaging__message-head">
                      <strong>{message.senderName}</strong>
                      {message.senderUserId === me ? (
                        <span className="messaging__message-actions">
                          {message.body ? (
                            <button
                              type="button"
                              onClick={() =>
                                beginEdit(message.id, message.body ?? "")
                              }
                            >
                              ویرایش
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="is-danger"
                            onClick={() => void removeMessage(message.id)}
                          >
                            حذف
                          </button>
                        </span>
                      ) : null}
                    </div>
                    {editingId === message.id ? (
                      <div className="messaging__edit">
                        <textarea
                          autoFocus
                          value={editingBody}
                          maxLength={4000}
                          onChange={(event) =>
                            setEditingBody(event.target.value)
                          }
                        />
                        <div>
                          <button
                            type="button"
                            disabled={busy || !editingBody.trim()}
                            onClick={() => void saveEdit()}
                          >
                            ذخیره
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(null);
                              setEditingBody("");
                            }}
                          >
                            انصراف
                          </button>
                        </div>
                      </div>
                    ) : message.body ? (
                      <p>{message.body}</p>
                    ) : null}
                    {message.attachments.length ? (
                      <div className="messaging__images">
                        {message.attachments.map((attachment) => (
                          <ProtectedImage
                            key={attachment.id}
                            api={props.api}
                            attachment={attachment}
                            onLoad={() => scrollToBottom()}
                          />
                        ))}
                      </div>
                    ) : null}
                    <small>
                      {jalali(message.createdAt)}
                      {message.senderUserId === me && message.seenByCount > 0
                        ? " · دیده شد"
                        : ""}
                      {message.editedAt ? " · ویرایش‌شده" : ""}
                    </small>
                  </article>
                ))}
                {detail.participants
                  .filter((p) => p.userId !== me)
                  .some((p) => p.isTyping) ? (
                  <div className="messaging__typing">در حال نوشتن…</div>
                ) : null}
              </div>
              <footer className="messaging__composer">
                {attachments.length ? (
                  <div className="messaging__picked">
                    {attachments.map((item) => (
                      <span key={item.fileName}>{item.fileName}</span>
                    ))}
                    <button type="button" onClick={() => setAttachments([])}>
                      حذف
                    </button>
                  </div>
                ) : null}
                <textarea
                  value={composer}
                  onChange={(event) => typing(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      void send();
                    }
                  }}
                  placeholder="پیام خود را بنویسید…"
                />
                <label className="messaging__attach" title="پیوست تصویر">
                  📎
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={(event) => void selectImages(event.target.files)}
                  />
                </label>
                <button
                  type="button"
                  disabled={busy || (!composer.trim() && !attachments.length)}
                  onClick={() => void send()}
                >
                  ارسال
                </button>
              </footer>
            </>
          ) : (
            <div className="messaging__empty messaging__empty--center">
              یک گفت‌وگو را انتخاب کنید یا پیام جدید بسازید.
            </div>
          )}
        </main>
      </div>
    </section>
  );
}
